import type { CurrentMerchantState } from '../../reducers/currentMerchant';
import type { MerchantRole } from '../types';

export function getHelpCenterLink({
  key = 'home',
  config,
  locale = 'EN',
  companyName = 'shore',
}: {
  key: string;
  locale?: string;
  companyName: string;
  config?: Record<string, Record<string, string>>;
}) {
  switch (companyName) {
    case 'shore':
      return (
        config?.helpcenter?.[key]?.replace(
          '__locale__',
          locale.toLowerCase().split('-')[0]
        ) ?? ''
      );
    case 'localsearch':
      return 'https://cc.localsearch.ch/s/help?language=de&topic=Product_related_questions_NV&subtopic=MyCOCKPIT_booking_system_NV';
    default:
      return null;
  }
}

export const isTrialPhasePromoActive = ({
  companyName,
  trialDaysLeft,
  roles,
}: {
  companyName: string;
  trialDaysLeft: number;
  roles: string[];
}) =>
  companyName === 'shore' &&
  trialDaysLeft !== 0 &&
  (roles.includes('admin') || roles.includes('owner'));

export const isUpsellShorePOSActive = ({
  locale,
  companyName,
  packageCodes,
}: {
  locale: string;
  companyName: string;
  packageCodes: string[];
}) => {
  if (companyName !== 'shore') {
    return false;
  }

  const availableLocales = ['en', 'de', 'de-CH', 'es'];

  if (availableLocales.indexOf(locale) === -1) {
    return false;
  }

  if (
    packageCodes.includes('shore_basic') ||
    packageCodes.includes('shore_plus')
  ) {
    return true;
  }

  if (packageCodes.length === 0) {
    return true;
  }

  return false;
};

export const isShorePOSExternalLinkActive = ({
  companyName,
  packageCodes,
}: {
  companyName: string;
  packageCodes: string[];
}): boolean => {
  const packageCodesEligibleForExternalPOSLink = [
    'shore_pos_premium',
    'shore_aio_starter',
    'shore_aio_growth',
    'shore_aio_teams',
  ];

  if (companyName !== 'shore') {
    return false;
  }

  const hasMatchingPackageCode = packageCodes.some((code) =>
    packageCodesEligibleForExternalPOSLink.includes(code)
  );

  if (hasMatchingPackageCode) {
    return true;
  }

  return false;
};

// TODO: update WhitelabelSlug in shore-gmbh/ts-types to include 'shore' — the
// backend sends it (see reducers/index.test.ts initializeSuccess payloads) even
// though the current union omits it. Once updated, narrow this param to
// WhitelabelSlug | null.
export const isWhitelabelAccount = (whiteLabel: string | null): boolean => {
  return whiteLabel !== null && whiteLabel !== 'shore';
};

export const isResellerAccount = (
  subscriptionPartner: string | null
): boolean => {
  const resellers = ['kreativmedia', 'herold'];
  return (
    subscriptionPartner !== null &&
    subscriptionPartner !== undefined &&
    resellers.includes(subscriptionPartner.toLowerCase())
  );
};

type IsAdminAccountInput = {
  organizationAdmin?: boolean;
  roles?: readonly MerchantRole[];
};

export const isAdmin = ({
  currentAccount,
  currentMerchant,
}: {
  currentAccount: IsAdminAccountInput;
  currentMerchant: Pick<CurrentMerchantState, 'id'>;
}): boolean => {
  const roles = currentAccount?.roles;
  return (
    currentAccount?.organizationAdmin ||
    roles
      ?.filter(({ merchant: { id } }) => id === currentMerchant.id)
      .reduce<boolean>(
        (acc, { name }) => acc || ['admin'].includes(name),
        false
      ) ||
    false
  );
};
